import { getMCPMonitorTopSources } from "@/api/endpoints/searches";
import { cn } from "@/lib/utils";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { Badge } from "@components/ui/badge";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
} from "@components/ui/widget";
import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { getMCPMonitorFiltersFromSearchFilterV4 } from "./utils";
import {
  Avatar,
  AvatarFallback,
  AvatarGroup,
  AvatarImage,
} from "@components/ui/avatar";
import { getInsights } from "@/api/endpoints/insights";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@components/ui/empty";
import { formatDateForApi } from "@/utils";
import { endOfWeek, startOfWeek } from "date-fns";
import ErrorComponent from "@components/ui/error";
import InsightText from "@/containers/InsightText";
import { Link } from "react-router-dom";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { postTrackingData } from "@/api/endpoints/track";
import { useInsightView } from "@/hooks/useInsightView";
import { useSheetLink } from "@/hooks/useSheetFilterParams";
import { EVENT } from "@/tracking_events";

export default function HighlightsWidget({
  searchUuid,
  className,
  filters,
  ...rest
}: WidgetProps & {
  filters: SearchFilterV4WithDates;
  searchUuid: Uuid;
}) {
  const { t } = useLingui();

  // URL state
  const insight = useInsightView();
  const getSheetLink = useSheetLink();

  // Helpers
  const weekStartDate = formatDateForApi(
    startOfWeek(new Date(), { weekStartsOn: 1 })
  );
  const weekEndDate = formatDateForApi(
    endOfWeek(new Date(), { weekStartsOn: 1 })
  );

  // Server state
  const { data: topSourcesData } = useQuery({
    queryKey: [searchUuid, "top-sources", filters],
    queryFn: () =>
      getMCPMonitorTopSources(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        start_date: weekStartDate,
        end_date: weekEndDate,
        metric: "mentions",
      }),
  });

  const { data: insights, isLoading: isInsightsLoading } = useQuery({
    queryKey: [searchUuid, "insights"],
    queryFn: () => getInsights(searchUuid),
  });

  // Handlers
  function handleSourceClick() {
    postTrackingData(EVENT.WIDGET_CLICK, {
      insight,
      widget: "highlights",
      action: "channel",
    });
  }

  if (!isInsightsLoading && insights?.ok && !insights.data.length) {
    return null;
  }

  return (
    <Widget className={cn("h-36 gap-2", className)} variant="purple" {...rest}>
      <WidgetHeader
        isLoading={isInsightsLoading}
        variant="purple"
        skeletonClassName="h-5.5"
      >
        <Badge variant="purple" intensity="strong" data-slot="card-title">
          <Trans>Veckans sammanfattning</Trans>
        </Badge>

        <AvatarGroup>
          {topSourcesData?.ok
            ? topSourcesData.data.sources.map((source) => (
                <Tooltip key={source.channel_id}>
                  <TooltipTrigger
                    render={
                      <Avatar
                        size="xs"
                        className="hover:z-10"
                        render={
                          source.channel_name ? (
                            <Link
                              replace
                              to={getSheetLink({
                                overrides: {
                                  channel_name: source.channel_name,
                                },
                                startDate: weekStartDate,
                                endDate: weekEndDate,
                              })}
                              onClick={handleSourceClick}
                              aria-label={source.channel_name}
                            />
                          ) : undefined
                        }
                      >
                        <AvatarImage
                          src={source.channel_artwork_url || undefined}
                        />
                        <AvatarFallback variant="purple">
                          {(source.channel_name || t`Okänd källa`)
                            .charAt(0)
                            .toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    }
                  />
                  <TooltipContent>
                    {source.channel_name || t`Okänd källa`}
                  </TooltipContent>
                </Tooltip>
              ))
            : null}
        </AvatarGroup>
      </WidgetHeader>
      <WidgetContent isLoading={isInsightsLoading} variant="purple">
        {insights ? (
          insights.ok ? (
            insights.data.length ? (
              <ul
                data-testid="insights-summary"
                className="list-disc list-inside flex flex-col gap-0.5 pl-2"
              >
                {insights.data.slice(0, 3).map((insight, i) => (
                  <li
                    key={`${insight.id}-${i}`}
                    data-testid={`insight-type-${insight.type}`}
                  >
                    {insight.link ? (
                      <Link
                        to={insight.link}
                        data-testid={`insight-type-${insight.type}-link`}
                      >
                        <InsightText insight={insight} />
                      </Link>
                    ) : (
                      <InsightText insight={insight} />
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <Empty className="pb-4 px-4 pt-0">
                <EmptyHeader>
                  <EmptyTitle>
                    <Trans>Inga insikter</Trans>
                  </EmptyTitle>
                  <EmptyDescription>
                    <Trans>Tyvärr finns inga insikter att visa just nu.</Trans>
                  </EmptyDescription>
                </EmptyHeader>
              </Empty>
            )
          ) : (
            <ErrorComponent
              hideIcon
              className="pb-4 px-4 pt-0"
              description={t`Något gick snett med att ladda insikterna. Vänligen försök igen senare.`}
            />
          )
        ) : isInsightsLoading ? null : (
          <ErrorComponent
            hideIcon
            className="pb-4 px-4 pt-0"
            description={t`Något gick snett med att ladda insikterna. Vänligen försök igen senare.`}
          />
        )}
      </WidgetContent>
    </Widget>
  );
}
